import { makeStyles } from '@mui/styles';
import type { DefaultTheme } from '@mui/styles/defaultTheme';
import {
  type SelectProps,
  type BoxProps,
  FormHelperText,
  Box,
  MenuItem,
  Select,
} from '@mui/material';
import { Show } from '../../../../../components/Show';

/**
 * A select with one option or none offers nothing to change to, and renders
 * dimmed. The Moo snapshot asks the same question so the agent is never told
 * such a field is editable.
 */
export const offersNoChoice = (options: readonly unknown[]): boolean =>
  options.length <= 1;

interface Props {
  options: { label: string; value: any }[];
  name: string;
  errorMessage?: string;
  boxProps?: BoxProps;
}

const SelectBase = ({
  options,
  sx = {},
  ...props
}: Props & Omit<SelectProps, 'variant'>) => {
  const classes = useSelectClasses();

  return (
    <Box position="relative" {...props.boxProps}>
      <Select
        classes={classes}
        {...props}
        sx={{
          backgroundColor: (theme: DefaultTheme) =>
            theme.palette.background.active,
          border: (theme) => theme.config.colors.darkGreySoft,
          opacity:
            props.disabled || offersNoChoice(options) ? '0.5 !important' : 1,
          ...sx,
        }}
      >
        {options.map((option) => (
          <MenuItem value={option.value} key={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <Show when={!!props.errorMessage}>
        <FormHelperText sx={{ marginTop: 0 }}>
          {props.errorMessage}
        </FormHelperText>
      </Show>
    </Box>
  );
};

export default SelectBase;

const useSelectClasses = makeStyles<DefaultTheme>(({ palette }) => ({
  select: {
    border: 'none',
    minWidth: '5.5rem',
    outline: 'none',
    borderRadius: 5,
    borderColor: palette.divider,
    '&:focus': {
      backgroundColor: palette.background.active,
    },
  },
}));
